import { updateExternalPrices } from "@lib/cart/getExternalPrices"
import { BigNumber } from "ethers"
import getTotalPrices, { reducerUsdc } from "./getTotalPrices"
import { _getAccountBalance } from "@lib/getAccountBalance"
import {
  GetERC20Allowance,
  SetERC20Approve,
  PayWithAuthorization
} from "@lib/chain"
import { constants } from "@src/constants"
import { getAddress } from "@/src/lib/getAddress"
import { waitForTransaction } from "@wagmi/core"
import { erc20ABI, useAccount } from "wagmi"
import {
  NewTransaction,
  payProducts,
  payProductsConfig
} from "@lib/chain/PayProducts/PayProducts"
import { sendMessage } from "@lib/xmtp"
import { ProductCart } from "./cartProductTypes"
import { formatEther } from "viem"
import { contractBatchWrite } from "react-walletkit"
import { View } from "@src/components/Modal/modals/types"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { ErrorState } from "@src/components/CheckoutButton/CheckoutButton"
import { useSliceContext } from "@src/components/SliceProvider"
import { useAddRecentTransaction } from "@rainbow-me/rainbowkit"
import { useConnectWithWalletKit, useSIWEAccount } from "react-walletkit"
import { zeroAddress } from "viem"
import { useToast } from "@/src/components/ui/base/use-toast"
import { ToastAction } from "@/src/components/ui/base/toast"
import { getSliceSubdomain } from ".."
import { updateAllowedItems } from "./getAllowedItems"
import { useEthersProvider } from "../ethers"

type HandleCheckoutTypes = {
  buyer: `0x${string}`
  account: `0x${string}`
  signerWallet: `0x${string}`
  cart: ProductCart[]
  updateCart: (newCart: ProductCart[]) => void
  setCheckoutCompleted: Dispatch<SetStateAction<boolean>>
  setLoadingState: Dispatch<SetStateAction<string>>
  setErrorState: Dispatch<SetStateAction<ErrorState>>
  modalView: View
  setModalView: Dispatch<SetStateAction<View>>
  chainId: number
  dynamicUsdcPrice: number
  addRecentTransaction: (transaction: NewTransaction) => void
  isSmartWallet: boolean
  onSuccess?: () => void | Promise<void>
  onError?: () => void | Promise<void>
  setHash: Dispatch<SetStateAction<string>>
  toast: ReturnType<typeof useToast>["toast"]
  receiver?: string
  refetchBalance: (options?: {
    throwOnError: boolean
    cancelRefetch: boolean
  }) => Promise<any>
}

export function useCheckout({
  onSuccess,
  onError
}: {
  onSuccess?: () => void | Promise<void>
  onError?: () => void | Promise<void>
} = {}) {
  const {
    appChainId,
    setIsUpdatingCart,
    cart,
    updateCart,
    modalView,
    setModalView,
    checkoutCompleted,
    setCheckoutCompleted,
    notificationReceiver,
    setIsReady,
    setHash,
    refetchBalance
  } = useSliceContext()
  const { toast } = useToast()
  const addRecentTransaction = useAddRecentTransaction()
  const provider = useEthersProvider()
  const { address: account, connector, isReconnecting } = useAccount()
  const { connect: connectWalletkit } = useConnectWithWalletKit()
  const { address: signerWallet, isLoading: isLoadingSmartWallet } =
    useSIWEAccount()
  const [loadingState, setLoadingState] = useState("")
  const [errorState, setErrorState] = useState<ErrorState>({ message: "" })
  const { totalPriceUsdc } = getTotalPrices(cart)
  let dynamicUsdcPrice = totalPriceUsdc
  const buyer = signerWallet || account
  const isSmartWallet = connector?.id === "walletkit"
  const isLoading = loadingState !== ""

  const checkout = async () => {
    if (account) {
      if (!isLoading && cart.length) {
        handleCheckout({
          cart,
          account,
          buyer,
          signerWallet,
          updateCart,
          setCheckoutCompleted,
          setLoadingState,
          setErrorState,
          modalView,
          setModalView,
          chainId: appChainId,
          dynamicUsdcPrice,
          addRecentTransaction,
          isSmartWallet,
          onSuccess,
          onError,
          setHash,
          toast,
          receiver: notificationReceiver,
          refetchBalance
        })
      }
    } else connectWalletkit()
  }

  const updateDynamicElements = async () => {
    setIsReady(false)
    setIsUpdatingCart(true)
    // TODO: do them in parallel

    let updatedCart = [...cart]

    // Update isAllowed
    const checkAllowedItems = cart.filter(
      (el) =>
        el.extCheckSig != "0x" &&
        el.extCheckSig != "0x00000000" &&
        el.extCheckAddress != zeroAddress
    )

    if (checkAllowedItems.length != 0) {
      updatedCart = await updateAllowedItems({
        cart,
        checkAllowedItems,
        buyer,
        provider
      })

      updateCart(updatedCart)
    }

    // Update prices
    const dynamicItems = cart.filter((el) => el.externalAddress != zeroAddress)

    if (dynamicItems.length != 0) {
      updatedCart = await updateExternalPrices({
        cart,
        dynamicItems,
        buyer,
        chainId: appChainId
      })
    }

    updateCart(updatedCart)

    setIsReady(true)
    setIsUpdatingCart(false)
  }

  const updateAllowedProducts = async () => {
    cart?.forEach((product) => {
      const { isAllowed } = product
      if (isAllowed === false) {
        setErrorState({ message: "Remove locked products to checkout" })
        return
      }
    })
  }

  useEffect(() => {
    setErrorState({
      message: ""
    })
    if (account && cart.length) {
      updateAllowedProducts()
    }
  }, [account, cart])

  useEffect(() => {
    if (isReconnecting || isLoadingSmartWallet) {
      setIsReady(false)
    } else {
      updateDynamicElements()
    }
  }, [buyer, isReconnecting, isLoadingSmartWallet])

  return {
    checkout,
    errorState,
    isLoading,
    loadingState,
    isSuccess: checkoutCompleted
  }
}

async function handleCheckout({
  buyer,
  cart,
  updateCart,
  setCheckoutCompleted,
  setLoadingState,
  setErrorState,
  modalView,
  setModalView,
  account,
  chainId,
  dynamicUsdcPrice,
  addRecentTransaction,
  signerWallet,
  isSmartWallet,
  onSuccess,
  onError,
  setHash,
  toast,
  receiver,
  refetchBalance
}: HandleCheckoutTypes) {
  setErrorState({ message: "" })
  setHash("")
  setLoadingState("Updating prices")

  const slicerId = cart[0].slicerId
  const usdc = constants[chainId].addresses["USDCPriceFeed"]
  const { totalPriceEth, totalPriceUsdc } = getTotalPrices(cart)
  const bridgeChainId = chainId === 8453 ? 1 : 5

  try {
    setCheckoutCompleted(false)
    let updatedCart: ProductCart[] = [...cart]

    const dynamicItems = cart.filter((el) => el.externalAddress != zeroAddress)

    if (dynamicItems.length != 0) {
      updatedCart = await updateExternalPrices({
        cart,
        dynamicItems,
        buyer,
        chainId
      })

      updateCart(updatedCart)
      dynamicUsdcPrice = updatedCart?.reduce(reducerUsdc, 0) || 0
    }

    const {
      ethBalance,
      usdcBalance,
      ethSenderBalance,
      usdcSenderBalance,
      ethSenderBridge
    } = await _getAccountBalance(account, signerWallet, bridgeChainId, chainId)

    // TODO: Test dynamicUsdcPrice / totalPriceUsdc in this block
    if (
      Number(formatEther(ethBalance)) < totalPriceEth ||
      usdcBalance < BigInt(dynamicUsdcPrice)
    ) {
      if (modalView?.name) {
        setModalView({
          name: ""
        })
      }
      setTimeout(() => {
        setModalView({
          name: "TOPUP_VIEW",
          cross: true,
          params: {
            ethBalance,
            usdcBalance,
            ethSenderBalance,
            usdcSenderBalance,
            ethSenderBridge,
            totalPriceEth,
            totalPriceUsdc: dynamicUsdcPrice,
            accountTopUpAddress: account,
            senderWallet: signerWallet
          }
        })
      }, 0)
    } else {
      try {
        const isUsdcProductPresent = updatedCart.some(
          (el) => el.currency != zeroAddress
        )

        const calls = []
        let hash: `0x${string}`

        // Approve USDC flow
        if (isUsdcProductPresent && totalPriceEth !== 0) {
          const allowance = await GetERC20Allowance(
            usdc as `0x${string}`,
            account,
            chainId
          )
          const allowanceRequired = BigNumber.from(totalPriceUsdc)

          if (allowance < BigInt(allowanceRequired.toHexString())) {
            const productsModuleAddress = getAddress(chainId, "ProductsModule")

            if (isSmartWallet) {
              calls.push({
                address: usdc as `0x${string}`,
                abi: erc20ABI,
                functionName: "approve",
                args: [
                  productsModuleAddress,
                  BigInt(allowanceRequired.toHexString())
                ]
              })
            } else {
              setLoadingState("Approve USDC")

              const hash = await SetERC20Approve(
                usdc as `0x${string}`,
                allowanceRequired,
                addRecentTransaction,
                chainId
              )

              setLoadingState("Approving")

              await waitForTransaction({ hash })
            }
          }
        }

        setLoadingState("Confirm purchase")

        // Pay with authorization
        if (totalPriceEth === 0) {
          hash = await PayWithAuthorization(
            account,
            dynamicUsdcPrice,
            buyer,
            updatedCart,
            chainId
          )

          addRecentTransaction?.({
            hash,
            description: "Checkout"
          })
        } else {
          // Smart wallet: Batch approve and checkout
          if (isSmartWallet && calls.length) {
            calls.push(await payProductsConfig(buyer, updatedCart, chainId))

            const { hash: txHash } = await contractBatchWrite({
              calls
            })

            hash = txHash as `0x${string}`

            addRecentTransaction?.({
              hash,
              description: "Approve USDC and Checkout"
            })
          } else {
            // Standard pay
            hash = await payProducts(
              buyer,
              updatedCart,
              addRecentTransaction,
              chainId
            )
          }
        }

        setLoadingState("Purchasing")
        await waitForTransaction({ hash })

        // Send message on Base Chain
        const isBaseChain = ["8453", "84531"].includes(chainId.toString())
        if (isBaseChain) {
          sendMessage(chainId, cart, account!, slicerId, receiver)
        }

        await onSuccess?.()

        toast({
          duration: 300000, // 5 minutes
          title: "Checkout successful",
          action: (
            <a
              href={`https://${getSliceSubdomain(
                chainId
              )}slice.so/slicer/${slicerId}`}
            >
              <ToastAction altText="Go to store">Go to store</ToastAction>
            </a>
          )
        })

        setHash(hash)
        refetchBalance()
        setCheckoutCompleted(true)
      } catch (err: any) {
        console.log(err)

        await onError?.()

        if (!err.message.includes("User rejected the request")) {
          // Evaluate if using toast
          // toast({
          //   title: "Error",
          //   description: err.message.includes("insufficient funds")
          //     ? "Insufficient funds"
          //     : "Transaction error",
          // })

          if (err.message.includes("insufficient funds")) {
            setErrorState({ message: "Insufficient funds", type: "error" })
          } else {
            setErrorState({ message: "Transaction error", type: "error" })
          }
          setTimeout(() => {
            setErrorState({ message: "" })
          }, 2000)
        }
      }
    }
  } catch (err) {
    console.log(err)
  }

  setLoadingState("")
}
